import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Divider,
  Chip,
  Paper,
  InputAdornment,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Slider,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Search as SearchIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Tag as ChannelIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  VideoCall as VideoCallIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  CloudUpload as LargeUploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { HuddleModal } from './HuddleModal';
import { LargeFileUploader } from './LargeFileUploader';
import { IncomingCallNotification } from './IncomingCallNotification';
import timezoneService from '../services/timezoneService';

interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface Channel {
  id: string;
  name: string;
  description?: string;
  type: 'public' | 'private' | 'direct';
  message_count: number;
  last_message?: string;
  last_message_at?: string;
  created_by_name: string;
}

interface DirectConversation {
  id: string;
  other_user_id: string;
  other_user_name: string;
  other_user_avatar?: string;
  last_message?: string;
  last_message_at?: string;
  message_count: number;
}

interface Message {
  id: string;
  content: string;
  message_type: string;
  ai_generated: boolean;
  created_at: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  reply_to?: string;
  reply_content?: string;
  reply_user_name?: string;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  job_title?: string;
  department?: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ open, onClose }) => {
  const { state: authState } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [channelToEdit, setChannelToEdit] = useState<Channel | null>(null);
  const [channelDescription, setChannelDescription] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [conversations, setConversations] = useState<DirectConversation[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [showNewChannelForm, setShowNewChannelForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedChannelForMenu, setSelectedChannelForMenu] = useState<Channel | null>(null);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState('');
  const [channelMembers, setChannelMembers] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHuddle, setShowHuddle] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showLargeFileUploader, setShowLargeFileUploader] = useState(false);
  
  // Reply and edit message state
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editContent, setEditContent] = useState('');
  const [messageMenuAnchor, setMessageMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMessageForMenu, setSelectedMessageForMenu] = useState<Message | null>(null);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    channelEmailNotifications: true,
    dmEmailNotifications: true,
    emailDigestInterval: 5,
  });
  const [savingNotificationSettings, setSavingNotificationSettings] = useState(false);
  const [notificationSettingsLoaded, setNotificationSettingsLoaded] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const unreadPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadData();
      startPolling();
    } else {
      stopPolling();
      // Reset selected channel when closing chat
      setSelectedChannel(null);
    }

    return () => {
      stopPolling();
    };
  }, [open]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start polling when component mounts for unread count
    startUnreadPolling();
    return () => {
      stopPolling();
    };
  }, []);

  // Load notification settings when Manage tab is opened
  useEffect(() => {
    if (activeTab === 2 && !notificationSettingsLoaded) {
      loadNotificationSettings();
    }
  }, [activeTab, notificationSettingsLoaded]);

  const startPolling = () => {
    stopPolling();
    pollingIntervalRef.current = setInterval(() => {
      if (selectedChannel) {
        loadMessages(selectedChannel);
      }
      // Only reload data if we don't have a selected channel to avoid interference
      if (!selectedChannel) {
        loadData();
      }
    }, 30000); // Poll every 30 seconds
  };

  const stopPolling = () => {
    // Stop message polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    // Stop unread count polling
    if (unreadPollingIntervalRef.current) {
      clearInterval(unreadPollingIntervalRef.current);
      unreadPollingIntervalRef.current = null;
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const unreadData = await apiService.getUnreadMessageCount();
      setUnreadCount(unreadData.count || 0);
    } catch (error: any) {
      // If 401 error, user is not authenticated - stop ALL polling
      if (error?.message?.includes('Access token required') || error?.message?.includes('401')) {
        console.warn('🔐 ChatSidebar: 401 error, stopping all polling (user not authenticated)');
        stopPolling(); // This now stops BOTH intervals
        return;
      }
      console.error('Error fetching unread count:', error);
    }
  };

  const startUnreadPolling = () => {
    // Clear any existing interval first
    if (unreadPollingIntervalRef.current) {
      clearInterval(unreadPollingIntervalRef.current);
    }
    
    // Store the interval ref so we can stop it later
    unreadPollingIntervalRef.current = setInterval(async () => {
      if (!open) {
        await fetchUnreadCount();
      }
    }, 30000); // Check for unread messages every 30 seconds
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [channelsData, conversationsData, teamData] = await Promise.all([
        apiService.getChatChannels(),
        apiService.getDirectConversations(),
        apiService.getChatTeamMembers(),
      ]);
      
      setChannels(channelsData);
      setConversations(conversationsData);
      setTeamMembers(teamData);
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const settings = await apiService.getNotificationSettings();
      setNotificationSettings({
        channelEmailNotifications: settings.channel_email_notifications,
        dmEmailNotifications: settings.dm_email_notifications,
        emailDigestInterval: settings.email_digest_interval,
      });
      setNotificationSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const saveNotificationSettings = async (newSettings: typeof notificationSettings) => {
    try {
      setSavingNotificationSettings(true);
      await apiService.updateNotificationSettings(newSettings);
      setNotificationSettings(newSettings);
    } catch (error) {
      console.error('Error saving notification settings:', error);
    } finally {
      setSavingNotificationSettings(false);
    }
  };

  const loadMessages = async (channelId: string) => {
    try {
      const messagesData = await apiService.getChatMessages(channelId);
      setMessages(messagesData);
      setSelectedChannel(channelId);
      
      // Mark messages as read when opening a channel
      try {
        console.log('📖 Marking messages as read for channel:', channelId);
        await apiService.markMessagesAsRead({ channelId });
        console.log('✅ Messages marked as read successfully');
        
        // Refresh unread count after marking messages as read
        await fetchUnreadCount();
        
        // Trigger a custom event to notify other components
        window.dispatchEvent(new CustomEvent('messages-read', { detail: { channelId } }));
      } catch (error) {
        console.error('Failed to mark messages as read:', error);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChannel) return;

    try {
      const message = await apiService.sendChatMessage(selectedChannel, {
        content: newMessage,
        messageType: 'text',
        replyTo: replyToMessage?.id,
      });
      
      setMessages(prev => [...prev, message]);
      setNewMessage('');
      setReplyToMessage(null); // Clear reply after sending
      
      // Update channel list
      loadData();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const createChannel = async () => {
    if (!newChannelName.trim()) return;

    try {
      await apiService.createChatChannel({
        name: newChannelName,
        description: '',
        type: 'public',
      });
      
      setNewChannelName('');
      setShowNewChannelForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating channel:', error);
    }
  };

  const startDirectConversation = async (userId: string) => {
    try {
      const conversation = await apiService.startDirectConversation(userId);
      await loadMessages(conversation.id);
      setActiveTab(1); // Switch to DM tab
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const updateChannel = async (channelId: string, updates: { name?: string; description?: string }) => {
    try {
      await apiService.updateChatChannel(channelId, updates);
      setChannelToEdit(null);
      setChannelDescription('');
      loadData();
    } catch (error) {
      console.error('Error updating channel:', error);
    }
  };

  const deleteChannel = async (channelId: string) => {
    if (!window.confirm('Are you sure you want to delete this channel? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteChatChannel(channelId);
      if (selectedChannel === channelId) {
        setSelectedChannel(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting channel:', error);
    }
  };

  const handleEditChannel = (channel: Channel) => {
    setChannelToEdit(channel);
    setChannelDescription(channel.description || '');
  };

  const handleSaveChannel = () => {
    if (channelToEdit) {
      updateChannel(channelToEdit.id, {
        name: channelToEdit.name,
        description: channelDescription,
      });
    }
  };

  const loadChannelMembers = async (channelId: string) => {
    try {
      const members = await apiService.getChatChannelMembers(channelId);
      setChannelMembers(members);
    } catch (error) {
      console.error('Error loading channel members:', error);
    }
  };

  const addMemberToChannel = async () => {
    if (!selectedMember || !selectedChannelForMenu) return;

    try {
      await apiService.addChatChannelMember(selectedChannelForMenu.id, selectedMember);
      setShowAddMemberDialog(false);
      setSelectedMember('');
      setSelectedChannelForMenu(null);
      // Reload members if dialog is still open
      if (selectedChannelForMenu) {
        loadChannelMembers(selectedChannelForMenu.id);
      }
    } catch (error) {
      console.error('Error adding member to channel:', error);
    }
  };

  const removeMemberFromChannel = async (channelId: string, memberId: string) => {
    try {
      await apiService.removeChatChannelMember(channelId, memberId);
      loadChannelMembers(channelId);
    } catch (error) {
      console.error('Error removing member from channel:', error);
    }
  };

  // Delete a message
  const handleDeleteMessage = async (message: Message) => {
    if (!selectedChannel) return;
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await apiService.deleteChatMessage(selectedChannel, message.id);
      setMessages(prev => prev.filter(m => m.id !== message.id));
      setMessageMenuAnchor(null);
      setSelectedMessageForMenu(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Kunde inte ta bort meddelandet.');
    }
  };

  // Edit a message
  const handleEditMessage = async () => {
    if (!selectedChannel || !editingMessage || !editContent.trim()) return;

    try {
      const updated = await apiService.editChatMessage(selectedChannel, editingMessage.id, editContent);
      setMessages(prev => prev.map(m => m.id === editingMessage.id ? { ...m, content: editContent } : m));
      setEditingMessage(null);
      setEditContent('');
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Kunde inte redigera meddelandet.');
    }
  };

  // Start editing a message
  const startEditMessage = (message: Message) => {
    setEditingMessage(message);
    setEditContent(message.content);
    setMessageMenuAnchor(null);
    setSelectedMessageForMenu(null);
  };

  // Reply to a message
  const handleReplyToMessage = (message: Message) => {
    setReplyToMessage(message);
    setMessageMenuAnchor(null);
    setSelectedMessageForMenu(null);
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedChannel) return;

    try {
      setUploadingFile(true);
      
      const messageType = file.type.startsWith('image/') ? 'image' : 'file';
      const result = await apiService.uploadChatFile(file, selectedChannel, messageType);
      
      // Add the uploaded file message to the chat
      if (result) {
        setMessages(prev => [...prev, result]);
        loadData(); // Refresh channel list
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Kunde inte ladda upp filen. Försök igen.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Filen är för stor. Maximal storlek är 10MB.');
        return;
      }
      
      handleFileUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTime = (timestamp: string) => {
    // eslint-disable-next-line no-restricted-syntax
    const date = new Date(timestamp); // Parse UTC timestamp
    // eslint-disable-next-line no-restricted-syntax
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    // Use timezoneService for dates older than 7 days
    const userDate = timezoneService.convertUTCToUserTime(timestamp);
    return timezoneService.formatUserDate(userDate);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredConversations = conversations.filter(conv =>
    conv.other_user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeamMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderChannelList = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Channels
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowNewChannelForm(true)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        {showNewChannelForm && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Channel name"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createChannel()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={createChannel}>
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <List dense>
          {filteredChannels.map((channel) => (
            <ListItemButton
              key={channel.id}
              selected={selectedChannel === channel.id}
              onClick={() => loadMessages(channel.id)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                },
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#e67e22' }}>
                  <ChannelIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={channel.message_count > 0 ? 700 : 500}
                      sx={{ 
                        color: channel.message_count > 0 ? '#1976d2' : 'inherit'
                      }}
                    >
                      #{channel.name}
                    </Typography>
                    {channel.message_count > 0 && (
                      <Chip
                        label={channel.message_count}
                        size="small"
                        sx={{ 
                          height: 16, 
                          fontSize: '0.7rem',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    noWrap
                    sx={{ 
                      fontWeight: channel.message_count > 0 ? 600 : 400,
                      color: channel.message_count > 0 ? '#333' : 'text.secondary'
                    }}
                  >
                    {channel.last_message || 'No messages yet'}
                  </Typography>
                }
              />
              {channel.last_message_at && (
                <Typography variant="caption" color="text.secondary">
                  {formatTime(channel.last_message_at)}
                </Typography>
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Box>
  );

  const renderDirectMessages = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Direct Messages
        </Typography>

        <List dense>
          {filteredConversations.map((conv) => (
            <ListItemButton
              key={conv.id}
              selected={selectedChannel === conv.id}
              onClick={() => loadMessages(conv.id)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={conv.other_user_avatar || undefined}
                  sx={{ width: 32, height: 32, bgcolor: '#e67e22' }}
                >
                  {conv.other_user_name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={conv.message_count > 0 ? 700 : 500}
                      sx={{ 
                        color: conv.message_count > 0 ? '#9c27b0' : 'inherit'
                      }}
                    >
                      {conv.other_user_name}
                    </Typography>
                    {conv.message_count > 0 && (
                      <Chip
                        label={conv.message_count}
                        size="small"
                        sx={{ 
                          height: 16, 
                          fontSize: '0.7rem',
                          backgroundColor: '#9c27b0',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    noWrap
                    sx={{ 
                      fontWeight: conv.message_count > 0 ? 600 : 400,
                      color: conv.message_count > 0 ? '#333' : 'text.secondary'
                    }}
                  >
                    {conv.last_message || 'No messages yet'}
                  </Typography>
                }
              />
              {conv.last_message_at && (
                <Typography variant="caption" color="text.secondary">
                  {formatTime(conv.last_message_at)}
                </Typography>
              )}
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Team Members
        </Typography>

        <List dense>
          {filteredTeamMembers.map((member) => (
            <ListItemButton
              key={member.id}
              onClick={() => startDirectConversation(member.id)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemAvatar>
                <Avatar
                  src={member.avatar ? (member.avatar.startsWith('http') ? member.avatar : `/api${member.avatar}`) : undefined}
                  sx={{ width: 32, height: 32 }}
                >
                  {member.name.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    {member.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {member.job_title || member.email}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Box>
  );

  const renderChannelManagement = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Channel Management
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search channels..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {channelToEdit ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Edit Channel
            </Typography>
            <TextField
              fullWidth
              label="Channel Name"
              value={channelToEdit.name}
              onChange={(e) => setChannelToEdit({ ...channelToEdit, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={channelDescription}
              onChange={(e) => setChannelDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSaveChannel}>
                Save
              </Button>
              <Button variant="outlined" onClick={() => setChannelToEdit(null)}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* Email Notification Settings */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                📧 Email Notifications
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.channelEmailNotifications}
                    onChange={(e) => {
                      const newSettings = {
                        ...notificationSettings,
                        channelEmailNotifications: e.target.checked,
                      };
                      saveNotificationSettings(newSettings);
                    }}
                    disabled={savingNotificationSettings}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Channel Messages</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get email digests for new channel messages
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.dmEmailNotifications}
                    onChange={(e) => {
                      const newSettings = {
                        ...notificationSettings,
                        dmEmailNotifications: e.target.checked,
                      };
                      saveNotificationSettings(newSettings);
                    }}
                    disabled={savingNotificationSettings}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Direct Messages</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get email notifications for DMs
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Digest Interval
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                How often to batch messages into digest emails (0 = immediate)
              </Typography>
              
              <Box sx={{ px: 1 }}>
                <Slider
                  value={notificationSettings.emailDigestInterval}
                  onChange={(_, value) => {
                    setNotificationSettings({
                      ...notificationSettings,
                      emailDigestInterval: value as number,
                    });
                  }}
                  onChangeCommitted={(_, value) => {
                    saveNotificationSettings({
                      ...notificationSettings,
                      emailDigestInterval: value as number,
                    });
                  }}
                  disabled={savingNotificationSettings || (!notificationSettings.channelEmailNotifications && !notificationSettings.dmEmailNotifications)}
                  min={0}
                  max={60}
                  step={5}
                  marks={[
                    { value: 0, label: 'Now' },
                    { value: 15, label: '15m' },
                    { value: 30, label: '30m' },
                    { value: 60, label: '1h' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => value === 0 ? 'Immediate' : `${value} min`}
                />
              </Box>
              
              {savingNotificationSettings && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Saving...
                </Typography>
              )}
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                All Channels
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowNewChannelForm(true)}
                sx={{ bgcolor: '#e67e22', color: 'white', '&:hover': { bgcolor: '#d35400' } }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {showNewChannelForm && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Create New Channel
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Channel name"
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && createChannel()}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={createChannel}>
                    Create
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setShowNewChannelForm(false)}>
                    Cancel
                  </Button>
                </Box>
              </Paper>
            )}

            <List dense>
              {filteredChannels.map((channel) => (
                <ListItem
                  key={channel.id}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#e67e22' }}>
                      <ChannelIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body1" 
                          fontWeight={channel.message_count > 0 ? 700 : 500}
                          sx={{ 
                            color: channel.message_count > 0 ? '#1976d2' : 'inherit'
                          }}
                        >
                          #{channel.name}
                        </Typography>
                        <Chip
                          label={channel.type}
                          size="small"
                          color={channel.type === 'public' ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {channel.message_count > 0 && (
                          <Chip
                            label={`${channel.message_count} unread`}
                            size="small"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontWeight: channel.message_count > 0 ? 600 : 400,
                            color: channel.message_count > 0 ? '#333' : 'text.secondary'
                          }}
                        >
                          {channel.description || 'No description'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            fontWeight: channel.message_count > 0 ? 600 : 400
                          }}
                        >
                          Created by {channel.created_by_name} • {channel.message_count} messages
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Tooltip title="Manage Members">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          setSelectedChannelForMenu(channel);
                          await loadChannelMembers(channel.id);
                          setShowAddMemberDialog(true);
                        }}
                        sx={{ bgcolor: '#4caf50', color: 'white', '&:hover': { bgcolor: '#388e3c' } }}
                      >
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setSelectedChannelForMenu(channel);
                      }}
                      sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => {
                setMenuAnchor(null);
                setSelectedChannelForMenu(null);
              }}
            >
              <MenuItem onClick={() => {
                if (selectedChannelForMenu) {
                  setChannelToEdit(selectedChannelForMenu);
                  setChannelDescription(selectedChannelForMenu.description || '');
                }
                setMenuAnchor(null);
                setSelectedChannelForMenu(null);
              }}>
                Edit Channel
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  if (selectedChannelForMenu) {
                    deleteChannel(selectedChannelForMenu.id);
                  }
                  setMenuAnchor(null);
                  setSelectedChannelForMenu(null);
                }}
                sx={{ color: '#d32f2f' }}
              >
                Delete Channel
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );

  const renderMessages = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {selectedChannel ? 
              channels.find(c => c.id === selectedChannel)?.name || 
              conversations.find(c => c.id === selectedChannel)?.other_user_name || 
              'Chat'
              : 'Select a channel'
            }
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedChannel && (
              <Tooltip title="Start Huddle">
                <IconButton 
                  size="small" 
                  onClick={async () => {
                    // Load channel members before starting huddle
                    try {
                      const members = await apiService.getChatChannelMembers(selectedChannel);
                      setChannelMembers(members);
                      setShowHuddle(true);
                    } catch (error) {
                      console.error('Error loading channel members for huddle:', error);
                      // Still allow huddle even if members fail to load
                      setShowHuddle(true);
                    }
                  }}
                  sx={{ 
                    bgcolor: '#4caf50', 
                    color: 'white',
                    '&:hover': { bgcolor: '#388e3c' }
                  }}
                >
                  <VideoCallIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setSelectedChannel(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              position: 'relative',
              '&:hover .message-actions': {
                opacity: 1,
              },
            }}
          >
            <Avatar
              src={message.user_avatar ? (message.user_avatar.startsWith('http') ? message.user_avatar : `/api${message.user_avatar}`) : undefined}
              sx={{ width: 32, height: 32 }}
            >
              {message.ai_generated ? (
                <AIIcon fontSize="small" />
              ) : (
                message.user_name.charAt(0).toUpperCase()
              )}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  {message.ai_generated ? 'AI Assistant' : message.user_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(message.created_at)}
                </Typography>
                {message.ai_generated && (
                  <Chip
                    label="AI"
                    size="small"
                    color="primary"
                    sx={{ height: 16, fontSize: '0.7rem' }}
                  />
                )}
                {/* Message action buttons - show on hover */}
                <Box 
                  className="message-actions"
                  sx={{ 
                    opacity: 0, 
                    transition: 'opacity 0.2s',
                    display: 'flex',
                    gap: 0.5,
                    ml: 'auto'
                  }}
                >
                  <Tooltip title="Reply">
                    <IconButton 
                      size="small" 
                      onClick={() => handleReplyToMessage(message)}
                      sx={{ p: 0.5, '&:hover': { bgcolor: '#e3f2fd' } }}
                    >
                      <ReplyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {message.user_id === authState.user?.id && message.message_type === 'text' && (
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        onClick={() => startEditMessage(message)}
                        sx={{ p: 0.5, '&:hover': { bgcolor: '#fff3e0' } }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {message.user_id === authState.user?.id && (
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteMessage(message)}
                        sx={{ p: 0.5, '&:hover': { bgcolor: '#ffebee' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {message.reply_to && (
                <Box sx={{ 
                  bgcolor: '#f5f5f5', 
                  p: 1, 
                  borderRadius: 1, 
                  mb: 1,
                  borderLeft: '3px solid #e67e22'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    Replying to {message.reply_user_name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {message.reply_content}
                  </Typography>
                </Box>
              )}
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: message.ai_generated ? '#e3f2fd' : '#f5f5f5',
                  borderRadius: 2,
                }}
              >
                {message.message_type === 'image' ? (
                  <Box>
                    <img
                      src={message.content}
                      alt="Shared image"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(message.content, '_blank')}
                    />
                  </Box>
                ) : message.message_type === 'file' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileIcon sx={{ color: '#666' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1976d2', 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': { color: '#1565c0' }
                        }}
                        onClick={() => window.open(message.content, '_blank')}
                      >
                        {(() => {
                          // Strip query params (?X-Amz-...) and timestamp prefix (1234567890-)
                          const raw = message.content.split('/').pop() || 'Nedladdad fil';
                          const withoutQuery = raw.split('?')[0];
                          return withoutQuery.replace(/^\d+-/, '') || withoutQuery;
                        })()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Klicka för att öppna filen
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography 
                    variant="body2" 
                    component="pre"
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'inherit',
                      margin: 0,
                      fontSize: 'inherit',
                      lineHeight: 1.5
                    }}
                  >
                    {message.content}
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {selectedChannel && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          {/* Reply indicator */}
          {replyToMessage && (
            <Box sx={{ 
              mb: 1, 
              p: 1, 
              bgcolor: '#e3f2fd', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '3px solid #1976d2'
            }}>
              <Box>
                <Typography variant="caption" color="primary" fontWeight={600}>
                  Replying to {replyToMessage.user_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                  {replyToMessage.content.substring(0, 50)}{replyToMessage.content.length > 50 ? '...' : ''}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyToMessage(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Edit mode indicator */}
          {editingMessage && (
            <Box sx={{ 
              mb: 1, 
              p: 1, 
              bgcolor: '#fff3e0', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '3px solid #ff9800'
            }}>
              <Box>
                <Typography variant="caption" color="warning.dark" fontWeight={600}>
                  Editing message
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
                  {editingMessage.content.substring(0, 50)}{editingMessage.content.length > 50 ? '...' : ''}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => { setEditingMessage(null); setEditContent(''); }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type a message... (mention @ai for AI help)"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title="Attach file (max 10MB)">
                        <IconButton 
                          size="small"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                          sx={{ 
                            color: uploadingFile ? '#ccc' : '#666',
                            '&:hover': { 
                              bgcolor: 'rgba(0,0,0,0.04)',
                              color: '#333'
                            }
                          }}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Upload large file (max 1GB)">
                        <IconButton 
                          size="small"
                          onClick={() => setShowLargeFileUploader(true)}
                          disabled={uploadingFile}
                          sx={{ 
                            color: uploadingFile ? '#ccc' : '#4caf50',
                            '&:hover': { 
                              bgcolor: 'rgba(76, 175, 80, 0.04)',
                              color: '#388e3c'
                            }
                          }}
                        >
                          <LargeUploadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send message">
                        <IconButton 
                          onClick={sendMessage} 
                          disabled={!newMessage.trim() || uploadingFile}
                          sx={{
                            color: (!newMessage.trim() || uploadingFile) ? '#ccc' : '#1976d2',
                            '&:hover': {
                              bgcolor: 'rgba(25, 118, 210, 0.04)',
                            }
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {uploadingFile && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="primary">
                Laddar upp fil...
              </Typography>
            </Box>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Press Enter to send, Shift+Enter for new line. Type @ai for AI assistance.
          </Typography>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt,.zip,.rar"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: '90vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="Channels" />
            <Tab label="Direct" />
            <Tab label="Manage" />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {selectedChannel ? (
            renderMessages()
          ) : (
            <>
              {activeTab === 0 && renderChannelList()}
              {activeTab === 1 && renderDirectMessages()}
              {activeTab === 2 && renderChannelManagement()}
            </>
          )}
        </Box>

        {/* Add Member Dialog */}
        <Dialog
          open={showAddMemberDialog}
          onClose={() => {
            setShowAddMemberDialog(false);
            setSelectedMember('');
            setSelectedChannelForMenu(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Manage Members - #{selectedChannelForMenu?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Add Team Member to Channel
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Member</InputLabel>
                <Select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  label="Select Member"
                >
                  {teamMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={member.avatar ? (member.avatar.startsWith('http') ? member.avatar : `/api${member.avatar}`) : undefined}
                          sx={{ width: 24, height: 24 }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{member.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.job_title || member.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedChannelForMenu && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Current Members
                </Typography>
                <List dense>
                  {channelMembers.map((member) => (
                    <ListItem
                      key={member.id}
                      sx={{
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        mb: 1,
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => removeMemberFromChannel(selectedChannelForMenu.id, member.id)}
                          size="small"
                          sx={{ color: '#d32f2f' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={member.avatar ? (member.avatar.startsWith('http') ? member.avatar : `/api${member.avatar}`) : undefined}
                          sx={{ width: 32, height: 32 }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={member.name}
                        secondary={member.role || 'Member'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowAddMemberDialog(false);
                setSelectedMember('');
                setSelectedChannelForMenu(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={addMemberToChannel}
              disabled={!selectedMember}
            >
              Add Member
            </Button>
          </DialogActions>
        </Dialog>

        {/* Huddle Modal */}
        <HuddleModal
          open={showHuddle && selectedChannel !== null}
          onClose={() => setShowHuddle(false)}
          channelId={selectedChannel || ''}
          channelName={
            selectedChannel ? (
              channels.find(c => c.id === selectedChannel)?.name || 
              conversations.find(c => c.id === selectedChannel)?.other_user_name || 
              'Chat'
            ) : 'Chat'
          }
        />

        {/* Large File Uploader Modal */}
        <Dialog
          open={showLargeFileUploader && selectedChannel !== null}
          onClose={() => setShowLargeFileUploader(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ p: 0 }}>
            <LargeFileUploader
              channelId={selectedChannel || ''}
              onUploadComplete={(message) => {
                setMessages(prev => [...prev, message]);
                setShowLargeFileUploader(false);
                loadData();
              }}
              onClose={() => setShowLargeFileUploader(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Incoming Call Notification - Shows when someone starts a huddle */}
        <IncomingCallNotification
          onJoinHuddle={(channelId, channelName) => {
            // Close drawer, set channel, and open huddle
            setSelectedChannel(channelId);
            loadMessages(channelId);
            setShowHuddle(true);
          }}
        />
      </Box>
    </Drawer>
  );
};
